import { GroupBase, StylesConfig } from 'react-select'

import { SelectOption } from '@/components'
import { colors, mainColors } from '@/styles'

// Step 1: Create base styles that work for all select components
const baseSelectStyles: StylesConfig<
	SelectOption,
	boolean,
	GroupBase<SelectOption>
> = {
	container: (baseStyles) => ({
		...baseStyles,
		width: '100%',
	}),
	control: (baseStyles, state) => ({
		...baseStyles,
		width: '100%',
		borderColor: state.isFocused ? mainColors.primary : baseStyles.borderColor,
		boxShadow: state.isFocused
			? `0 0 0 1px ${mainColors.primary}`
			: baseStyles.boxShadow,
		'&:hover': {
			borderColor: state.isFocused
				? mainColors.primary
				: baseStyles.borderColor,
		},
	}),
	menu: (provided) => ({
		...provided,
		zIndex: 10000,
	}),
	menuPortal: (provided) => ({
		...provided,
		zIndex: 10000,
	}),
	// Add more common styles here
}

// Step 2: Create component-specific style modifiers
const asyncSelectStyles: StylesConfig<
	SelectOption,
	boolean,
	GroupBase<SelectOption>
> = {
	// Styles specific to AsyncSelect (can override base styles)
	// For example, you might need to adjust menu styling for custom components
	menuList: (baseStyles) => ({
		...baseStyles,
		// Special styling for AsyncSelect menu list...
	}),
	option: (baseStyles, state) => {
		const { base, light } = colors.primary

		return {
			...baseStyles,
			background: state.isSelected ? base : state.isFocused ? light : '#fff',
			':hover': {
				...baseStyles[':hover'],
				background: state.isSelected ? '#000' : light,
			},
			':active': {
				...baseStyles[':active'],
				background: state.isSelected ? '#000' : light,
			},
		}
	},
	// Any other AsyncSelect-specific overrides
}

const selectStyles: StylesConfig<
	SelectOption,
	boolean,
	GroupBase<SelectOption>
> = {
	// Styles specific to AsyncSelect (can override base styles)
	// For example, you might need to adjust menu styling for custom components
	menuList: (baseStyles) => ({
		...baseStyles,
	}),
	option: (baseStyles, state) => ({
		...baseStyles,
		backgroundColor: state.isSelected
			? mainColors.primary
			: state.isFocused
				? `${mainColors.primary}20`
				: baseStyles.backgroundColor,
		color: state.isSelected ? 'white' : baseStyles.color,
		'&:active': {
			backgroundColor: state.isSelected
				? mainColors.primary
				: `${mainColors.primary}40`,
		},
	}),
	// Any other AsyncSelect-specific overrides
}

// Step 3: Create style factory functions for each component type

interface StylesConfigOptions {
	stylesConfig?: StylesConfig<SelectOption, boolean, GroupBase<SelectOption>>
	// You can add more options that affect styling here
}

// For regular Select components
export function getSelectStyles({
	stylesConfig = {},
}: StylesConfigOptions = {}) {
	return {
		...baseSelectStyles,
		...selectStyles,
		...stylesConfig,
	}
}

// For AsyncSelect components
export function getAsyncSelectStyles({
	stylesConfig = {},
}: StylesConfigOptions = {}): StylesConfig<
	SelectOption,
	boolean,
	GroupBase<SelectOption>
> {
	return {
		...baseSelectStyles,
		...asyncSelectStyles, // Apply AsyncSelect-specific modifications
		...stylesConfig, // Apply custom instance modifications
	}
}
